import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/hooks/use-toast"
import { useMutation, gql } from "@apollo/client"

const TOURNAMENT_TYPE_UPDATE = gql`
  mutation TournamentTypeUpdate($TournamentTypeUpdateInput: TournamentTypeUpdateInput!) {
    TournamentTypeUpdate(input: $TournamentTypeUpdateInput) {
      status {
        success
        name
        detail
      }
      TournamentType {
        id
        name
        description
        sortOrder
        status
        minPlayers
        maxTopPlayers
        multiplier
        winnerPoints
        loserPoints
        drawPoints
        socialRequired
        urlImage
      }
    }
  }
`

interface TournamentTypeEditFormProps {
  tournamentType?: any
  onSuccess: () => void
  onCancel: () => void
}

export function TournamentTypeEditForm({
  tournamentType,
  onSuccess,
  onCancel,
}: TournamentTypeEditFormProps) {
  const { toast } = useToast()
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: true,
    private: false,
    minPlayers: 2,
    maxTopPlayers: 2,
    multiplier: 1,
    winnerPoints: 3,
    loserPoints: 0,
    drawPoints: 1,
    socialRequired: false,
    storeTournaments: 1,
    canBeDouble: false,
  })

  const [updateTournamentType, { loading }] = useMutation(TOURNAMENT_TYPE_UPDATE, {
    onCompleted: (data) => {
      if (data.TournamentTypeUpdate.status.success) {
        toast({
          title: "Éxito",
          description: "Tipo de torneo actualizado correctamente",
          variant: "success",
        })
        onSuccess()
      } else {
        toast({
          title: "Error",
          description: data.TournamentTypeUpdate.status.detail || "Error al actualizar",
          variant: "destructive",
        })
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  useEffect(() => {
    if (tournamentType) {
      setFormData({
        name: tournamentType.name || "",
        description: tournamentType.description || "",
        status: Boolean(tournamentType.status),
        private: tournamentType.private || false,
        minPlayers: tournamentType.minPlayers || 4,
        maxTopPlayers: tournamentType.maxTopPlayers || 8,
        multiplier: tournamentType.multiplier || 0,
        winnerPoints: tournamentType.winnerPoints || 3,
        loserPoints: tournamentType.loserPoints || 0,
        drawPoints: tournamentType.drawPoints || 1,
        socialRequired: tournamentType.socialRequired || false,
        storeTournaments: tournamentType.storeTournaments || 1,
        canBeDouble: tournamentType.canBeDouble || false,
      })
    }
  }, [tournamentType])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!tournamentType) {
      toast({
        title: "Error",
        description: "De momento solo permitimos editar tipos de torneos",
        variant: "destructive",
      })
      return // Only editing for now
    }

    await updateTournamentType({
      variables: {
        TournamentTypeUpdateInput: {
          id: tournamentType.id,
          ...formData,
        },
      },
    })
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nombre</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="minPlayers">Mínimo de Jugadores</Label>
          <Input
            id="minPlayers"
            type="number"
            min="4"
            value={formData.minPlayers}
            onChange={(e) => handleInputChange("minPlayers", parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="winnerPoints">Puntos Victoria</Label>
          <Input
            id="winnerPoints"
            type="number"
            min="1"
            value={formData.winnerPoints}
            onChange={(e) => handleInputChange("winnerPoints", parseInt(e.target.value))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="loserPoints">Puntos Derrota</Label>
          <Input
            id="loserPoints"
            type="number"
            min="0"
            value={formData.loserPoints}
            onChange={(e) => handleInputChange("loserPoints", parseInt(e.target.value))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="drawPoints">Puntos Empate</Label>
          <Input
            id="drawPoints"
            type="number"
            min="0"
            value={formData.drawPoints}
            onChange={(e) => handleInputChange("drawPoints", parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="multiplier">Multiplicador</Label>
          <Input
            id="multiplier"
            type="number"
            step="1"
            min="0"
            max="3"
            value={formData.multiplier}
            onChange={(e) => handleInputChange("multiplier", parseFloat(e.target.value))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="maxTopPlayers">Máximo Top Jugadores</Label>
          <Input
            id="maxTopPlayers"
            type="number"
            min="2"
            value={formData.maxTopPlayers}
            onChange={(e) => handleInputChange("maxTopPlayers", parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="status"
            checked={formData.status}
            onCheckedChange={(checked) => handleInputChange("status", checked)}
          />
          <Label htmlFor="status">Activo</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="private"
            checked={formData.private}
            onCheckedChange={(checked) => handleInputChange("private", checked)}
          />
          <Label htmlFor="private">Privado</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="socialRequired"
            checked={formData.socialRequired}
            onCheckedChange={(checked) => handleInputChange("socialRequired", checked)}
          />
          <Label htmlFor="socialRequired">Redes Sociales Requeridas</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="canBeDouble"
            checked={formData.canBeDouble}
            onCheckedChange={(checked) => handleInputChange("canBeDouble", checked)}
          />
          <Label htmlFor="canBeDouble">Puede ser Doble</Label>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </form>
  )
}
